import { DUEL_STAT_PROFILES, POWER_CURVE_EXPONENT, type DuelLevel } from '../battle/aiSkill';
import { createRng } from '../rng/seededRandom';
import { attackerWinProbability, effectiveHitPoints } from './battle';
import { pieceById } from './movement';
import type { BattleResult, FactionId, GameContext, GameState, Piece } from './types';

export interface RollBattleOptions {
  /** Human-controlled factions should be omitted: they use the fixed Expert baseline. */
  levels?: Partial<Record<FactionId, DuelLevel>>;
}

/**
 * The Roll curve (`GameDefinition.rollCurve`): a fight between two seats at `level` is settled on
 * `Φ(c·Φ⁻¹(p))` of its odds, one of Roll's own settlement settings (policy v7, section 5). Under
 * one the favourite's edge shrinks; at one, the default, the odds come back exactly.
 */
export function rollCurve(ctx: GameContext, p: number, level: DuelLevel): number {
  const c = ctx.definition?.rollCurve?.[level] ?? 1;
  return c === 1 ? p : normalCdf(c * inverseNormal(p));
}

function adjustedPairProbability(
  ctx: GameContext,
  state: GameState,
  attackerId: number,
  defenderId: number,
  options: RollBattleOptions,
): number {
  const attacker = pieceById(state, attackerId)!;
  const defender = pieceById(state, defenderId)!;
  const target = state.pendingBattle!;
  // Two seats at one level read that level's measured odds (phase D); the factor below is then
  // nothing. Seats without a level are Expert, here as in the factor.
  const base = attackerWinProbability(
    ctx,
    { ...state, pendingBattle: { attackerId, defenderId, x: target.x, y: target.y } },
    options.levels ?? {},
  );
  const la = options.levels?.[attacker.faction] ?? 'expert';
  const ld = options.levels?.[defender.faction] ?? 'expert';
  // Two seats at one level: the level's Roll curve, in a Roll game (the Arena's AI reads these
  // shares for a muster too, and keeps the plain odds).
  const bent = ctx.combatMode === 'roll' && la === ld ? rollCurve(ctx, base, la) : base;
  const a = DUEL_STAT_PROFILES[la].powerGoal;
  const d = DUEL_STAT_PROFILES[ld].powerGoal;
  // Levels enter as combat power on the acceptance curve, so a roll one level up wins the same
  // 80 % of even fights that the arena ladder gives.
  const odds = (bent / Math.max(1e-9, 1 - bent)) * (a / d) ** POWER_CURVE_EXPONENT;
  return odds / (1 + odds);
}

/** Apply each side's configured AI level exactly once, as an odds rather than a second stat pass. */
export function rollWinProbability(
  ctx: GameContext,
  state: GameState,
  options: RollBattleOptions = {},
): number {
  const pending = state.pendingBattle;
  if (!pending) return 0;
  const attacker = pieceById(state, pending.attackerId);
  const defender = pieceById(state, pending.defenderId);
  if (!attacker || !defender) return 0;
  return adjustedPairProbability(ctx, state, attacker.id, defender.id, options);
}

/**
 * Each participant's mean performance in a clash: its edge over every other participant on the
 * normal scale the pair's odds give, averaged over the clash. The roll adds a standard-normal draw
 * to each, and the best performance wins.
 */
function performanceMeans(
  ctx: GameContext,
  state: GameState,
  participants: readonly Piece[],
  options: RollBattleOptions,
): number[] {
  return participants.map(
    (piece) =>
      participants.reduce((sum, opponent) => {
        if (piece.id === opponent.id) return sum;
        const p = adjustedPairProbability(ctx, state, piece.id, opponent.id, options);
        return sum + Math.SQRT2 * inverseNormal(p);
      }, 0) / participants.length,
  );
}

/** Half-width of the band the top-draw integral covers, in standard deviations, and its steps. */
const SHARE_REACH = 8;
const SHARE_STEPS = 512;

/**
 * The chance each participant of the pending clash has of winning it, in participant order: the
 * probability that its performance draw comes out on top in `resolveRollBattle`. A fight of three
 * or more (a muster) is where the board AI needs it; the integral runs over the winner's own draw,
 * on a fixed grid, so the same state always gives the same shares.
 */
export function rollWinShares(
  ctx: GameContext,
  state: GameState,
  options: RollBattleOptions = {},
): number[] {
  const pending = state.pendingBattle;
  if (!pending) return [];
  const participants = (pending.participantIds ?? [pending.attackerId, pending.defenderId])
    .map((id) => pieceById(state, id))
    .filter((piece): piece is Piece => !!piece);
  const means = performanceMeans(ctx, state, participants, options);
  const step = (2 * SHARE_REACH) / SHARE_STEPS;
  const shares = means.map((mean, i) => {
    let total = 0;
    for (let k = 0; k <= SHARE_STEPS; k++) {
      const z = -SHARE_REACH + k * step;
      let top = normalDensity(z) * (k === 0 || k === SHARE_STEPS ? 0.5 : 1);
      for (let j = 0; j < means.length; j++) if (j !== i) top *= normalCdf(mean - means[j]! + z);
      total += top;
    }
    return total * step;
  });
  const sum = shares.reduce((a, b) => a + b, 0);
  return shares.map((share) => share / sum);
}

/**
 * Resolve Roll mode with a seeded standard-normal performance draw. The inverse-normal threshold
 * makes the long-run result equal the calibrated Arena odds while individual clashes still vary.
 */
export function resolveRollBattle(
  ctx: GameContext,
  state: GameState,
  options: RollBattleOptions = {},
): { result: BattleResult; rng: number; probability: number; performance: number } {
  const pending = state.pendingBattle;
  if (!pending) throw new Error('No battle to resolve');
  const rng = createRng(state.rng);
  const ids = pending.participantIds ?? [pending.attackerId, pending.defenderId];
  const participants = ids.map((id) => pieceById(state, id)!).filter(Boolean);
  const probability = rollWinProbability(ctx, state, options);
  const means = performanceMeans(ctx, state, participants, options);
  const performances = participants.map((piece, i) => {
    const u1 = Math.max(Number.EPSILON, rng.next());
    const u2 = rng.next();
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    return { piece, value: means[i]! + z };
  });
  performances.sort(
    (a, b) => b.value - a.value || ids.indexOf(a.piece.id) - ids.indexOf(b.piece.id),
  );
  const winner = performances[0]!.piece;
  const loser = performances[1]!.piece;
  const winnerHp = effectiveHitPoints(ctx, state, winner, pending.x, pending.y);
  const loserPower =
    effectiveHitPoints(ctx, state, loser, pending.x, pending.y) *
    (ctx.roster[loser.character]?.attack.damage ?? 1);
  const winnerPower = winnerHp * (ctx.roster[winner.character]?.attack.damage ?? 1);
  const wounds = Math.min(
    winnerHp - 1,
    Math.round(winnerHp * Math.min(1, loserPower / winnerPower) * (0.3 + 0.5 * rng.next())),
  );
  return {
    result: { winnerId: winner.id, winnerDamage: winner.damage + wounds },
    rng: rng.state(),
    probability,
    performance: performances[0]!.value,
  };
}

function normalDensity(z: number): number {
  return Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);
}

/** The standard normal distribution function. */
function normalCdf(z: number): number {
  return 0.5 * erfc(-z / Math.SQRT2);
}

/** Numerical Recipes' `erfcc`, fitted by Chebyshev: fractional error below 1.2e-7 everywhere. */
const ERFC_TERMS = [
  -1.26551223, 1.00002368, 0.37409196, 0.09678418, -0.18628806, 0.27886807, -1.13520398, 1.48851587,
  -0.82215223, 0.17087277,
];

function erfc(x: number): number {
  const t = 1 / (1 + 0.5 * Math.abs(x));
  let series = 0;
  for (let i = ERFC_TERMS.length - 1; i >= 0; i--) series = ERFC_TERMS[i]! + t * series;
  const y = t * Math.exp(-x * x + series);
  return x >= 0 ? y : 2 - y;
}

/** Acklam's rational approximation; plenty precise for a displayed game die. */
function inverseNormal(p: number): number {
  const q = Math.min(1 - 1e-12, Math.max(1e-12, p));
  const a = [
    -39.6968302866538, 220.946098424521, -275.928510446969, 138.357751867269, -30.6647980661472,
    2.50662827745924,
  ];
  const b = [
    -54.4760987982241, 161.585836858041, -155.698979859887, 66.8013118877197, -13.2806815528857,
  ];
  const c = [
    -0.00778489400243029, -0.322396458041136, -2.40075827716184, -2.54973253934373,
    4.37466414146497, 2.93816398269878,
  ];
  const d = [0.00778469570904146, 0.32246712907004, 2.445134137143, 3.75440866190742];
  if (q < 0.02425) {
    const r = Math.sqrt(-2 * Math.log(q));
    return (
      (((((c[0]! * r + c[1]!) * r + c[2]!) * r + c[3]!) * r + c[4]!) * r + c[5]!) /
      ((((d[0]! * r + d[1]!) * r + d[2]!) * r + d[3]!) * r + 1)
    );
  }
  if (q > 0.97575) return -inverseNormal(1 - q);
  const r = q - 0.5;
  const s = r * r;
  return (
    ((((((a[0]! * s + a[1]!) * s + a[2]!) * s + a[3]!) * s + a[4]!) * s + a[5]!) * r) /
    (((((b[0]! * s + b[1]!) * s + b[2]!) * s + b[3]!) * s + b[4]!) * s + 1)
  );
}
